import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, ArrowRightLeftIcon, EyeIcon, TruckIcon, ClockIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { DataTable, Column } from '../components/DataTable';
import { mockStockTransfers } from '../data/mockData';
import { StockTransfer } from '../types';
type TabFilter = 'all' | 'Draft' | 'Pending' | 'In Transit' | 'Completed' | 'Cancelled';
export function StockTransferList() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const completedCount = mockStockTransfers.filter(t => t.status === 'Completed').length;
  const pendingCount = mockStockTransfers.filter(t => t.status === 'Pending').length;
  const inTransitCount = mockStockTransfers.filter(t => t.status === 'In Transit').length;
  const cancelledCount = mockStockTransfers.filter(t => t.status === 'Cancelled').length;
  const filteredData = activeTab === 'all' ? mockStockTransfers : mockStockTransfers.filter(t => t.status === activeTab);
  const statusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'In Transit':
        return 'bg-blue-100 text-blue-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const columns: Column<StockTransfer>[] = [{
    key: 'transferNo',
    label: 'Transfer No'
  }, {
    key: 'date',
    label: 'Date'
  }, {
    key: 'product',
    label: 'Product'
  }, {
    key: 'productCode',
    label: 'Code'
  }, {
    key: 'fromWarehouse',
    label: 'From → To',
    render: item => <div className="flex items-center gap-1.5 text-sm" data-id="element-2685">
          <span className="font-medium text-gray-900" data-id="element-2686">
            {item.fromWarehouse}
          </span>
          <ArrowRightLeftIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" data-id="element-2687" />
          <span className="font-medium text-gray-900" data-id="element-2688">{item.toWarehouse}</span>
        </div>
  }, {
    key: 'quantity',
    label: 'Qty',
    render: item => <span className="font-medium" data-id="element-2689">
          {item.quantity} {item.unitOfMeasure}
        </span>
  }, {
    key: 'reason',
    label: 'Reason'
  }, {
    key: 'performedBy',
    label: 'Performed By'
  }, {
    key: 'status',
    label: 'Status',
    render: item => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor(item.status)}`} data-id="element-2690">
          {item.status}
        </span>
  }];
  const tabs: {
    key: TabFilter;
    label: string;
    count: number;
  }[] = [{
    key: 'all',
    label: 'All',
    count: mockStockTransfers.length
  }, {
    key: 'Pending',
    label: 'Pending',
    count: pendingCount
  }, {
    key: 'In Transit',
    label: 'In Transit',
    count: inTransitCount
  }, {
    key: 'Completed',
    label: 'Completed',
    count: completedCount
  }, {
    key: 'Cancelled',
    label: 'Cancelled',
    count: cancelledCount
  }];
  return <div className="space-y-6" data-id="element-2691">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2692">
        <div data-id="element-2693">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2694">Stock Transfers</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2695">
            Manage inventory transfers between warehouses.
          </p>
        </div>
        <button onClick={() => navigate('/stock/transfer/create')} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-2696">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-2697" />
          New Transfer
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2698">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2699">
          <div className="flex items-center" data-id="element-2700">
            <div className="flex-shrink-0 p-3 bg-green-100 rounded-lg" data-id="element-2701">
              <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-2702" />
            </div>
            <div className="ml-4" data-id="element-2703">
              <p className="text-sm font-medium text-gray-500" data-id="element-2704">Completed</p>
              <p className="text-2xl font-bold text-green-600" data-id="element-2705">
                {completedCount}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2706">
          <div className="flex items-center" data-id="element-2707">
            <div className="flex-shrink-0 p-3 bg-yellow-100 rounded-lg" data-id="element-2708">
              <ClockIcon className="h-6 w-6 text-yellow-600" data-id="element-2709" />
            </div>
            <div className="ml-4" data-id="element-2710">
              <p className="text-sm font-medium text-gray-500" data-id="element-2711">Pending</p>
              <p className="text-2xl font-bold text-yellow-600" data-id="element-2712">
                {pendingCount}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2713">
          <div className="flex items-center" data-id="element-2714">
            <div className="flex-shrink-0 p-3 bg-blue-100 rounded-lg" data-id="element-2715">
              <TruckIcon className="h-6 w-6 text-blue-600" data-id="element-2716" />
            </div>
            <div className="ml-4" data-id="element-2717">
              <p className="text-sm font-medium text-gray-500" data-id="element-2718">In Transit</p>
              <p className="text-2xl font-bold text-blue-600" data-id="element-2719">
                {inTransitCount}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2720">
          <div className="flex items-center" data-id="element-2721">
            <div className="flex-shrink-0 p-3 bg-red-100 rounded-lg" data-id="element-2722">
              <XCircleIcon className="h-6 w-6 text-red-600" data-id="element-2723" />
            </div>
            <div className="ml-4" data-id="element-2724">
              <p className="text-sm font-medium text-gray-500" data-id="element-2725">Cancelled</p>
              <p className="text-2xl font-bold text-red-600" data-id="element-2726">
                {cancelledCount}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Filters */}
      <div className="border-b border-gray-200" data-id="element-2727">
        <nav className="-mb-px flex space-x-8" data-id="element-2728">
          {tabs.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.key ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2729">
              {tab.label}
              <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${activeTab === tab.key ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-2730">
                {tab.count}
              </span>
            </button>)}
        </nav>
      </div>

      {/* Data Table */}
      <div className="h-[500px]" data-id="element-2731">
        <DataTable columns={columns} data={filteredData} keyExtractor={item => item.id} searchPlaceholder="Search transfers..." actions={item => <button onClick={() => alert(`View ${item.transferNo}`)} className="text-indigo-600 hover:text-indigo-900 flex items-center" data-id="element-2733">
              <EyeIcon className="h-4 w-4 mr-1" data-id="element-2734" /> View
            </button>} data-id="element-2732" />
      </div>
    </div>;
}